/* stylelint-disable */
import styled, { css } from 'styled-components';
import { layout, media, colorUtils } from '@prisjakt/ui-base';
import {
  EmoticonAngry,
  EmoticonDissatisfied,
  EmoticonNeutral,
  EmoticonSmiling,
  EmoticonLaughing,
} from '@prisjakt/ui-icons';

import { Variants, Colors } from '.';

const CHECKBOX_SIZE = {
  DESKTOP: 20,
  MOBILE: 32,
};

const disabledInputStyles = css`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

type MultiRadioProps = {
  variant: Variants;
  showError?: boolean;
  hasSelected?: boolean;
};

export const Inputs = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

export const MultiRadio = styled.div<MultiRadioProps>(
  p => css`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${p.variant === Variants.rate &&
      css`
        flex-direction: row;
      `};

    ${p.variant === Variants.scale &&
      css`
        display: inline-flex;
        position: relative;
        height: ${p.hasSelected ? '80px' : '60px'};
        align-items: flex-start;
        width: 100%;

        ${media.mediumDown`
          height: ${p.hasSelected ? '90px' : '70px'};
        `};

        ${media.mediumUp`
          max-width: 400px;
        `};

        ${Inputs} {
          width: 100%;
          justify-content: space-between;

          &::after {
            content: '';
            width: 100%;
            position: absolute;
            top: ${CHECKBOX_SIZE.DESKTOP / 2}px;
            z-index: 1;
            border-top: 1px solid
              ${p.showError ? p.theme.text.negative : p.theme.lines.tertiary};

            ${media.mediumDown`
            top: ${CHECKBOX_SIZE.MOBILE / 2}px;
          `};
          }
        }
      `};

    ${p.variant === Variants.boolean &&
      css`
        display: inline-flex;
        background: #eee;
        padding: 2px;
        border-radius: 4px;

        box-shadow: ${p.showError
          ? `0 0 0 1px ${p.theme.text.negative}`
          : 'none'};
      `};
  `
);

type SelectedRatingProps = {
  color: Colors;
};

export const SelectedRating = styled.div<SelectedRatingProps>(
  ({ color, theme }) => css`
    margin-left: 16px;
    border-radius: 20px;
    background: ${theme.ratings[color].bg};
    padding: 1px 8px;
    color: ${theme.ratings[color].fg};
  `
);

export const Selected = styled.div`
  height: 20px;
  max-height: 20px;
  font-size: ${layout.measurements.fontSizeSmall};
  margin-bottom: ${layout.spacing.xxSmall};
`;

type IconProps = {
  showError?: boolean;
};

const iconStyling = css<IconProps>`
  background: #eee;
  border-radius: 5px;
  box-shadow: ${p => (p.showError ? `0 0 0 1px ${p.theme.text.negative}` : 0)};
`;

export const EmoticonAngryIcon = styled(EmoticonAngry)`
  ${iconStyling}
`;

export const EmoticonDissatisfiedIcon = styled(EmoticonDissatisfied)`
  ${iconStyling}
`;

export const EmoticonNeutralIcon = styled(EmoticonNeutral)`
  ${iconStyling}
`;

export const EmoticonSmilingIcon = styled(EmoticonSmiling)`
  ${iconStyling}
`;

export const EmoticonLaughingIcon = styled(EmoticonLaughing)`
  ${iconStyling}
`;

export const RadioRating = styled.input`
  ${disabledInputStyles};

  & + label {
    cursor: pointer;

    &:not(:last-of-type) {
      margin-right: 16px;
    }
  }

  &:checked + label {
    ${EmoticonAngryIcon} {
      background: ${p => p.theme.ratings.negative.bg};
    }

    ${EmoticonDissatisfiedIcon} {
      background: ${p => p.theme.ratings.negative.bg};
    }

    ${EmoticonNeutralIcon} {
      background: ${p => p.theme.ratings.neutral.bg};
    }

    ${EmoticonSmilingIcon} {
      background: ${p => p.theme.ratings.positive.bg};
    }

    ${EmoticonLaughingIcon} {
      background: ${p => p.theme.ratings.positive.bg};
    }
  }

  &.focus-visible + label {
    ${EmoticonAngryIcon},
    ${EmoticonDissatisfiedIcon},
    ${EmoticonNeutralIcon},
    ${EmoticonSmilingIcon},
    ${EmoticonLaughingIcon} {
      box-shadow: 0 0 0 2px ${p => p.theme.icons.callToAction};
    }
  }
`;

type ScaleRadioProps = {
  alignRight: boolean;
  alignLeft: boolean;
  showError?: boolean;
  textLabel?: string;
  upscale?: boolean;
};

export const ScaleRadio = styled.input<ScaleRadioProps>(
  p => css`
    ${disabledInputStyles};

    & + label {
      display: inline-block;
      cursor: pointer;
      width: ${CHECKBOX_SIZE.DESKTOP}px;
      height: ${CHECKBOX_SIZE.DESKTOP}px;
      background: ${p.theme.bg.primary};
      z-index: 2;
      border-radius: 6px;
      border: 1px solid
        ${p.showError ? p.theme.text.negative : p.theme.lines.quaternary};

      ${media.mediumDown`
        width: ${CHECKBOX_SIZE.MOBILE}px;
        height: ${CHECKBOX_SIZE.MOBILE}px;
      `};

      position: relative;

      &::after {
        content: attr(data-label);
        position: absolute;
        align-self: center;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: ${layout.spacing.xSmall};
        color: ${p.theme.text.quaternary};
        width: 100px;
        font-size: ${layout.measurements.fontSizeSmall};
        text-align: center;

        ${p.alignRight &&
          css`
            right: 0;
            left: unset;
            transform: translateX(0);
            text-align: right;
          `};

        ${p.alignLeft &&
          css`
            left: 0;
            transform: translateX(0);
            text-align: left;
          `};
      }
    }

    &:checked + label {
      background: ${p.theme.lines.highlight};
      border: 1px solid ${p.theme.lines.highlight};
    }

    &.focus-visible + label {
      border: 2px solid ${p.theme.icons.callToAction};
    }
  `
);

export const BooleanRadio = styled.input(
  p => css`
    ${disabledInputStyles};

    & + label {
      cursor: pointer;
      padding: ${layout.spacing.xxSmall} ${layout.spacing.large};
      border-radius: 5px;
      white-space: nowrap;
      user-select: none;

      ${media.mediumDown`
        padding: ${layout.spacing.xSmall} ${layout.spacing.medium};
      `};
    }

    &:checked + label {
      background: #fff;
    }

    &.focus-visible + label {
      box-shadow: 0 0 0 2px ${p.theme.icons.callToAction};
    }
  `
);

type ErrorProps = {
  background?: boolean;
};

export const Error = styled.div<ErrorProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${p => (p.background ? layout.spacing.small : 0)};
  margin-top: ${layout.spacing.xSmall};
  color: ${p => p.theme.text.negative};
  background: ${p =>
    p.background
      ? colorUtils.setRgbaAlpha(p.theme.text.negative, '0.1')
      : 'none'};

  svg {
    margin-right: ${layout.spacing.xSmall};
  }
`;
